import { zodResolver } from "@hookform/resolvers/zod";
import { createId } from "@paralleldrive/cuid2";
import { ArrowLeftIcon, CheckIcon, SparkleIcon } from "@phosphor-icons/react";
import { defaultExperience, experienceSchema, type Experience } from "@reactive-resume/schema";
import {
  DateField,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RichInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { getCountryCities } from "@reactive-resume/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useAuthStore } from "@/client/stores/auth";

import { FreeTextInput } from "../shared/free-text-input";
import { SmartCombobox, type SmartComboboxOption } from "../shared/smart-combobox";
import { formatExperiencePeriod } from "./date-utils";
import { CONTRACT_OPTIONS, POSITION_OPTIONS } from "./suggestions";

type FormValues = z.infer<typeof experienceSchema>;
type Props = { item?: Experience; onCancel: () => void; onSave: (value: Experience) => void };

export const ExperienceForm = ({ item, onCancel, onSave }: Props) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(experienceSchema),
    mode: "onChange",
    defaultValues: { ...defaultExperience, id: createId(), ...item } as FormValues,
  });
  const startDate = (form.watch("startDate" as keyof FormValues) as string) ?? "";
  const endDate = (form.watch("endDate" as keyof FormValues) as string) ?? "";
  const isPresent = Boolean(form.watch("isPresent" as keyof FormValues));
  const [dateError, setDateError] = useState(false);
  const handleDateError = useCallback((value: boolean) => setDateError(value), []);
  const countryCode = useAuthStore((state) => state.user?.countryCode);
  const locationOptions = useMemo<SmartComboboxOption[]>(
    () =>
      [...getCountryCities(countryCode), "Télétravail"].map((city) => ({
        value: city,
        label: city,
      })),
    [countryCode],
  );

  useEffect(() => {
    form.reset({ ...defaultExperience, id: createId(), ...item } as FormValues);
  }, [form, item]);
  useEffect(() => {
    form.setValue(
      "date" as keyof FormValues,
      formatExperiencePeriod(startDate, endDate, isPresent) as never,
    );
  }, [endDate, form, isPresent, startDate]);

  return (
    <div className="rounded-2xl border border-indigo-100 bg-white p-3.5 shadow-[0_10px_30px_rgba(30,27,75,0.05)] sm:p-4">
      <div className="mb-4 flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
        <div>
          <h3 className="text-sm font-bold text-indigo-950">
            {item ? "Modifier l’expérience" : "Nouvelle expérience"}
          </h3>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Les suggestions se précisent pendant votre saisie.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-8 items-center gap-1 rounded-lg px-2 text-[10px] font-bold text-slate-500 hover:bg-slate-50"
        >
          <ArrowLeftIcon size={13} /> Liste
        </button>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((values) => {
            if (!dateError) onSave(values as Experience);
          })}
          className="grid gap-3.5 sm:grid-cols-2"
        >
          <FormField
            name="position"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Poste occupé
                </FormLabel>
                <FormControl>
                  <SmartCombobox
                    value={field.value ?? ""}
                    options={POSITION_OPTIONS}
                    placeholder="Ex. Développeur frontend"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="company"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">Entreprise</FormLabel>
                <FormControl>
                  <FreeTextInput
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Nom de l’entreprise"
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name="location"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">Lieu</FormLabel>
                <FormControl>
                  <SmartCombobox
                    value={field.value ?? ""}
                    options={locationOptions}
                    placeholder="Ville ou télétravail"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <FormField
            name={"contractType" as keyof FormValues}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[11px] font-bold text-indigo-950">
                  Type de contrat
                </FormLabel>
                <Select value={(field.value as string) ?? ""} onValueChange={field.onChange}>
                  <SelectTrigger className="min-h-10 rounded-xl bg-white text-xs">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_OPTIONS.map((contract) => (
                      <SelectItem key={contract} value={contract}>
                        {contract}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <div className="sm:col-span-2">
            <DateField
              label="Période"
              presentLabel="J’occupe actuellement ce poste"
              startDate={startDate}
              endDate={endDate}
              isPresent={isPresent}
              onStartDateChange={(value) =>
                form.setValue("startDate" as keyof FormValues, value as never, {
                  shouldValidate: true,
                })
              }
              onEndDateChange={(value) =>
                form.setValue("endDate" as keyof FormValues, value as never, {
                  shouldValidate: true,
                })
              }
              onPresentChange={(value) =>
                form.setValue("isPresent" as keyof FormValues, value as never)
              }
              onError={handleDateError}
            />
            {dateError && (
              <p className="mt-1 text-[10px] font-medium text-rose-600">
                La date de fin doit être postérieure à la date de début.
              </p>
            )}
          </div>
          <FormField
            name="summary"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <FormLabel className="text-[11px] font-bold text-indigo-950">
                    Responsabilités et résultats
                  </FormLabel>
                  <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-violet-600">
                    <SparkleIcon size={11} weight="fill" /> Assistance IA
                  </span>
                </div>
                <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={field.onChange}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        onChange={(value) => {
                          editor.commands.setContent(value, true);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </FormControl>
                <p className="text-[9px] leading-4 text-slate-400">
                  Précisez vos actions, leur contexte et leurs résultats mesurables.
                </p>
                <FormMessage className="text-[10px]" />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3 sm:col-span-2">
            <button
              type="button"
              onClick={onCancel}
              className="min-h-10 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={dateError}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800 disabled:opacity-40"
            >
              <CheckIcon size={14} weight="bold" />
              {item ? "Enregistrer" : "Ajouter"}
            </button>
          </div>
        </form>
      </Form>
    </div>
  );
};
